import { Hub } from "aws-amplify";
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { WorkspaceWithLicense } from "@examspark/api";
import { getTagFromResource } from "@examspark/common";
import { Workspace } from "@examspark/types";

import { useQueryListUserWorkspaces } from "../api/operations/user";
import { useQueryGetWorkspace } from "../api/operations/workspace";
import { currentAuthenticatedUser } from "../auth";
import { AnalyticsEventName, useAnalytics } from "../hooks/useAnalytics";
import { useLogout } from "../hooks/useLogout";
import { useAppContext } from "../providers/AppContext";
import { safeSessionStorage } from "../safeStorage";

import AppLoading from "./AppLoading";
import JoinWorkspace from "./workspace/JoinWorkspace";
import SelectWorkspace from "./workspace/SelectWorkspace";

const StyledJoin = styled.div`
  max-width: 40rem;
  margin: auto;

  h1,
  h2 {
    text-align: center;
  }
  h2 {
    color: #666;
    font-weight: normal;
  }
`;

export const requiresChildWorkspaceSelection = (
  workspace: Workspace | WorkspaceWithLicense,
) => {
  return (
    getTagFromResource(workspace, "require-child-workspace-selection")
      ?.value === "TRUE"
  );
};

export const filterChildWorkspaces = (
  workspaces: WorkspaceWithLicense[],
  parentWorkspaceId: string | null,
) => {
  return (
    workspaces?.filter((workspace) => {
      if (requiresChildWorkspaceSelection(workspace.workspace)) return false;
      if (!parentWorkspaceId) return true;

      if (workspace.workspace.workspaceParentId !== parentWorkspaceId) {
        return false;
      }

      return true;
    }) ?? []
  );
};

/**
 * Common application component that enforces the existence of authenticated
 * user, otherwise redirect to login screen.
 */
const ProtectedRoutes: React.FC<
  React.PropsWithChildren<
    PropsWithChildren<{
      onSignOut?: () => void;
      redirectOnLogin?: boolean;
      workspacePromptHeader?: React.ReactNode;
    }>
  >
> = (props) => {
  const [state, dispatch] = useAppContext();
  const analytics = useAnalytics();
  const defaultRedirectUrl = "/login";
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const pathname = location.pathname;
  const [isLoaded, setIsLoaded] = useState(false);

  // postpone queries until we have a _confirmed_ user
  const workspaceQuery = useQueryGetWorkspace(
    isLoaded ? state.workspace?.id : undefined,
  );
  const { data: userWorkspaces, refetch: refetchUserWorkspaces } =
    useQueryListUserWorkspaces({
      userId: isLoaded ? state.user?.id : undefined,
      appId: state.app?.id,
    });

  const redirectToLogin = useCallback(() => {
    if (props.redirectOnLogin) {
      safeSessionStorage.setItem("redirect_on_login", window.location.pathname);

      navigate(defaultRedirectUrl + "?redirect=true");
    } else {
      navigate(defaultRedirectUrl);
    }
  }, [navigate, props.redirectOnLogin]);

  useEffect(() => {
    currentAuthenticatedUser().then((user) => {
      if (
        user != null &&
        state.user != null &&
        user.attributes.sub === state.user.id
      ) {
        setIsLoaded(true);
        return;
      }

      // handle case where user is not authenticated or local state no longer
      // contains correct user data
      logout().then(() => {
        redirectToLogin();
      });
    });
  }, [logout, redirectToLogin, state.user]);

  // analytics: identify user
  const { identify } = analytics;
  useEffect(() => {
    if (state.user?.id == null) return;
    identify(state.user);
  }, [identify, state.user]);

  // analytics: track every page navigation
  useEffect(() => {
    analytics.trackEvent(AnalyticsEventName.PageView, {
      page: pathname,
    });
  }, [analytics, pathname]);

  useEffect(() => {
    const cancelToken = Hub.listen("auth", ({ payload }) => {
      // console.debug(payload.event);
      if (payload.event === "signOut") {
        // console.log("User signed out (expired tokens?). Redirecting to login.");
        setIsLoaded(false);
        logout().then(() => {
          navigate(defaultRedirectUrl);
        });
      }
    });
    return cancelToken;
  }, [logout, navigate]);

  useEffect(() => {
    if (state.app?.id == null || state.workspace?.id == null) {
      redirectToLogin();
    }
  }, [redirectToLogin, state.app?.id, state.workspace?.id]);

  // in order to prevent expired user sessions from temporarily showing the
  // page (but all queries failing), we wait until we confirm the user is
  // authenticated before rendering the page
  if (!isLoaded) {
    return <AppLoading text={`Loading ${state.app?.name}`} />;
  }

  // Do not render the children until we have determined the workspace and
  // whether we need to require the user to select a child workspace.
  if (!workspaceQuery.data || workspaceQuery.isFetching || !userWorkspaces) {
    return <AppLoading text={`Loading ${state.app?.name}`} />;
  }

  // console.log("workspace", workspaceQuery.data);
  const handleJoin = async (workspaceId: string) => {
    refetchUserWorkspaces();
  };

  const handleSelectWorkspace = (workspaceUser: WorkspaceWithLicense) => {
    dispatch({
      type: "SELECT_WORKSPACE_WITH_LICENSE",
      data: workspaceUser,
    });
  };

  /**
   * If our current workspace is a parent workspace that is not meant to be
   * used directly, but rather for organizing child workspaces, then force user
   * to select a child workspace.
   */
  if (requiresChildWorkspaceSelection(workspaceQuery.data)) {
    const filteredUserWorkspaces = filterChildWorkspaces(
      userWorkspaces,
      workspaceQuery.data.id,
    );

    if (filteredUserWorkspaces?.length === 1) {
      handleSelectWorkspace(filteredUserWorkspaces[0]);
      return <AppLoading text={`Loading ${state.app?.name}`} />;
    } else if (filteredUserWorkspaces?.length > 1) {
      return (
        <StyledJoin>
          <SelectWorkspace
            header={props.workspacePromptHeader}
            onSelect={handleSelectWorkspace}
            workspaces={filteredUserWorkspaces}
          />
        </StyledJoin>
      );
    } else {
      return (
        <StyledJoin>
          <JoinWorkspace
            onJoin={handleJoin}
            header={props.workspacePromptHeader}
          />
        </StyledJoin>
      );
    }
  }

  return <>{props.children}</>;
};

export default ProtectedRoutes;
